import Layout from "@/components/feature/Layout";
import PageHero from "@/components/base/PageHero";
import SectionHeader from "@/components/base/SectionHeader";
import CapabilityCard from "@/components/base/CapabilityCard";
import CTAPanel from "@/components/base/CTAPanel";
import RelatedContent from "@/components/feature/RelatedContent";

const capabilities = [
  {
    title: "Enterprise Infrastructure & Network Engineering",
    description: "LAN/WAN engineering, server and collaboration support, VMware and vCenter deployment, virtualization, infrastructure modernization, and platform migration for enterprise environments.",
    icon: "ri-server-line",
    href: "/capabilities/infrastructure-network-engineering",
    tags: ["Proven Core", "LAN/WAN", "VMware"],
    featured: true,
  },
  {
    title: "Database & Data Platform Services",
    description: "Database engineering, administration, and platform support for Oracle, SQL Server, and relational database environments including schema management and data integrity.",
    icon: "ri-folder-3-line",
    href: "/capabilities/database-data-platform",
    tags: ["Proven Core", "Oracle", "SQL Server"],
    featured: true,
  },
  {
    title: "Application & Platform Engineering",
    description: "Enterprise application support, platform engineering, WebSphere support, application maintenance, data development, and integration support.",
    icon: "ri-layout-grid-line",
    href: "/capabilities/application-platform-engineering",
    tags: ["Proven Core", "WebSphere", "Integration"],
    featured: true,
  },
  {
    title: "Cloud Services",
    description: "Cloud engineering and migration built on Microsoft Azure, spanning IaaS, PaaS, hybrid cloud, storage, security, and cloud-native DevOps.",
    icon: "ri-cloud-line",
    href: "/capabilities/cloud-services",
    tags: ["Proven Core", "Microsoft Azure", "Cloud Migration"],
    featured: true,
  },
  {
    title: "Microsoft Power Platform & SharePoint",
    description: "Power Apps, Power Automate, Power BI, SharePoint workflows, and user enablement for enterprise business applications.",
    icon: "ri-window-line",
    href: "/capabilities/microsoft-solutions",
    tags: ["Power Platform", "SharePoint"],
  },
  {
    title: "Web & Digital Solutions",
    description: "Website development, CMS solutions, web portals, content migration, and accessibility-aware development for modern digital environments.",
    icon: "ri-global-line",
    href: "/capabilities/web-digital-solutions",
    tags: ["CMS"],
  },
  {
    title: "Mobile Application Development",
    description: "Custom mobile application development, cross-platform solutions, API integration, and accessibility-aware mobile UX for enterprise and government programs.",
    icon: "ri-smartphone-line",
    href: "/capabilities/mobile-application-development",
    tags: ["Mobile", "Application Engineering"],
  },
  {
    title: "AI & Intelligent Engineering",
    description: "AI-assisted application engineering, data pipelines, business intelligence, workflow automation, and machine learning integration.",
    icon: "ri-ai-generate",
    href: "/capabilities/ai-intelligent-engineering",
    tags: ["AI", "Data & Analytics"],
  },
];

export default function CapabilitiesOverview() {
  return (
    <Layout>
      <PageHero
        title="Capabilities"
        subtitle="What We Deliver"
        description="Mitaja Corporation delivers enterprise technology and engineering services through prime-contractor and teaming relationships."
        size="md"
      />

      <section className="section-padding bg-white">
        <div className="container-page">
          <SectionHeader
            title="Core Capabilities"
            description="Proven capabilities delivered across federal and commercial engagements."
          />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-14">
            {capabilities.filter(c => c.tags?.some(t => t === "Proven Core")).map((cap) => (
              <CapabilityCard key={cap.title} {...cap} />
            ))}
          </div>

          <SectionHeader
            title="Supporting Capabilities"
            description="Additional technology services that complement our core practice areas."
          />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {capabilities.filter(c => !c.tags?.some(t => t === "Proven Core")).map((cap) => (
              <CapabilityCard key={cap.title} {...cap} />
            ))}
          </div>
        </div>
      </section>

      <CTAPanel
        title="Need a Specialized Capability?"
        description="Mitaja provides qualified technical resources and engineering services for government and enterprise programs."
        secondaryAction={{ label: "Contact Us", href: "/contact" }}
        variant="dark"
      />
      <RelatedContent
        title="Past Performance, Experience, and Contracting Info"
        description="Explore our documented past performance, experience, and contracting information."
        links={[
          {
            label: "U.S. Government Experience",
            description: "Documented federal, state and local government technology delivery through subcontracting relationships.",
            href: "/experience/usa-government",
            icon: "ri-government-line",
          },
          {
            label: "Experience Overview",
            description: "Browse technology delivery experience across federal, international, and commercial environments.",
            href: "/experience",
            icon: "ri-line-chart-line",
          },
          {
            label: "Government Contracting Information",
            description: "UEI, CAGE, NAICS codes, GSA MAS, and procurement details for government buyers and partners.",
            href: "/resources/contracting-information",
            icon: "ri-file-shield-2-line",
          },
        ]}
      />
    </Layout>
  );
}